import { injectable } from 'inversify'
import { ApiService } from '@/service/ApiService'
import { IUser } from '@/model/user'
import TYPES from '@/config/Types'
import { DependencyContainer } from '@/config/DependencyContainer'
import { SessionHandler } from '@/service/AuthHandler';
import { UserService } from '@/service/UserService';
import { IConfig } from '@/config/Config';
import { WebAuthContainer } from '@/service/WebAuthContainer';

export interface AuthService {
  handleAuthCallback (): Promise<boolean>
  verifyToken (token: string): Promise<IUser|null>
  preAuth (): Promise<IUser | null>
  attemptAuth (email: string, password: string): Promise<IUser | null>
  signup (user: IUser): Promise<IUser>
  facebookLogin (): Promise<IUser | null>
  facebookAuth (access_token: string): Promise<IUser | null>
  updateUser (user: IUser): Promise<IUser>
  login (): void
  logout (): Promise<boolean>
  saveState (state: {page: string, id: string|null|undefined, uid: string|null|undefined}): void
  restoreState (): {page: string, id: string|null|undefined, uid: string|null|undefined}
}

@injectable()
export class AuthServiceImpl implements AuthService {
  private apiService: ApiService | null | undefined
  private userService: UserService | null | undefined
  private sessionHandler: SessionHandler | null | undefined
  private config: IConfig | null

  private webAuthContainer: WebAuthContainer

  constructor () {
    this.sessionHandler = DependencyContainer.getContainer().get<SessionHandler>(TYPES.SessionHandler)
    this.apiService = DependencyContainer.getContainer().get<ApiService>(TYPES.ApiService)
    this.userService = DependencyContainer.getContainer().get<UserService>(TYPES.UserService)
    this.webAuthContainer = DependencyContainer.getContainer().get<WebAuthContainer>(TYPES.WebAuthContainer)
    this.config = DependencyContainer.getContainer().get<IConfig>(TYPES.IConfig)
  }

  public saveState (state: {page: string, id: string|null|undefined, uid: string|null|undefined}): void {
    console.log('saveState: %j', state)
    this.getSessionHandler().setValue('page', state.page)
    if (state.id) {
      this.getSessionHandler().setValue('id', state.id)
    } else {
      this.getSessionHandler().clearValue('id')
    }
    if (state.uid) {
      this.getSessionHandler().setValue('uid', state.uid)
    } else {
      this.getSessionHandler().clearValue('uid')
    }
  }

  public restoreState (): {page: string, id: string|null|undefined, uid: string|null|undefined} {
    const page: string = this.getSessionHandler().getValue('page')
    const id = this.getSessionHandler().getValue('id')
    const uid = this.getSessionHandler().getValue('uid')
    const state = {page: page, id: id, uid: uid}
    this.getSessionHandler().clearValue('page')
    this.getSessionHandler().clearValue('id')
    this.getSessionHandler().clearValue('uid')
    console.log('restoreState: %j', state)
    return state
  }

  public login (): void {
    this.webAuthContainer.webAuth.authorize()
  }

  public renewLogin (): void {
    this.webAuthContainer.webAuth.authorize({prompt: 'none'})
  }

  public handleAuthCallback (): Promise<boolean> {
    this.restoreState();
    return new Promise<boolean>((resolve, reject) => {
      this.webAuthContainer.webAuth.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.getSessionHandler().setSession(authResult)
          return resolve(true)
        } else if (err) {
          console.log("handleAuthCallback: Error: %j", err.error)
          return resolve(false)
          // alert(`Error: ${err.error}. Check the console for further details.`)
        } else {
          console.log("handleAuthCallback: Unable to resolve user")
          return resolve(false)
        }
      })
    })
  }

  public logout (): Promise<boolean> {
    const logoutOptions = {
      clientID: process.env.VUE_APP_AUTH0_CLIENTID,
      returnTo: process.env.VUE_APP_AUTH0_LOGOUT_REDIRECT
    }
    return new Promise<boolean>((resolve, reject) => {
      this.getSessionHandler().clearSession()
      this.webAuthContainer.webAuth.logout({

      })
      console.log('Logged out')
      return resolve(true)
    })
  }

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  public verifyToken (token: string): Promise<IUser|null> {
    return new Promise<IUser|null>((resolve, reject) => {
      this.getSessionHandler().setToken(token)
      return this.preAuth()
    })
  }
/* 
  public reAuth (): Promise<IUser|null> {
    return new Promise<IUser|null>((resolve, reject) => {
      const token = this.getSessionHandler().getToken()
      if(token) {
        this.getUserService().getUserByToken(<string>token).then((user: IUser | null) => {
          if(user) {
            return getProfile(user)
          } else {
            console.log('preAUth: No user found')
            return resolve(null)
          }
        }).catch((err) => {
          console.log('preAuth: Error getting auth0 user: %j', err)
          return resolve(null)
        })
      }
    })
  }
  public preAuth (): Promise<IUser|null> {
    return new Promise<IUser|null>((resolve, reject) => {
    })
  })
 */

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  public preAuth (state?: {page: string, id: string|null|undefined, uid: string|null|undefined}): Promise<IUser|null> {
    return new Promise<IUser|null>((resolve, reject) => {
      // If JWT detected, attempt to get & store user's info
      if (this.getSessionHandler().isAuthenticated()) {
        this.getUserService().getMe().then((user: IUser | null) => {
          if(user) {
            return resolve(user)
          } else {
            console.log('preAuth: No user found')
            this.renewLogin()
          }
        }).catch((err) => {
          console.log('preAuth: Error getting auth0 user: %j', err)
          return resolve(null)
        })
      } else {
        if (this.getSessionHandler().hasToken()) {
          console.log('preAuth: Renewing token')
          this.renewLogin()
        }
        console.log('purging auth')
        return resolve(null)
      }
    })
  }

  public facebookLogin (): Promise<IUser | null> {
    console.log('AuthService: facebookLogin...')
    return new Promise<IUser | null>((resolve, reject) => {
      return resolve(null)
/* 
      this.getVueAuth().authenticate('facebook').then((user: IUser | null) => {
        // console.log('calling fb auth for response: %j', authResponse)
        return resolve(user)
      })
 */      
    })
  }

  public facebookAuth (access_token: string): Promise<IUser | null> {
    console.log('AuthService: facebookAuth...')
    return new Promise<IUser>((resolve, reject) => {
      this.getApiService().post<IUser>('/auth/facebook', { access_token: access_token, redirect_uri: (<IConfig>this.config).getFacebookRedirectUri() })
      .then((user: IUser) => {
        return resolve(user)
      })
    })
  }

  public attemptAuth (email: string, password: string): Promise<IUser | null> {
  // return this.getApiService().post<IUser>('/auth/login', { user: user })
    return new Promise<IUser | null>((resolve, reject) => {
      this.getApiService().post<IUser>('/auth/signin', { 'email': email, 'password': password })
      .then((user: IUser) => {
        if(user.token) {
          this.getSessionHandler().setToken(user.token)
          return resolve(user)
        } else {
          this.getSessionHandler().clearToken()
          return reject('User has no token')
        }
      })
    })
  }

  public signup (user: any): Promise<IUser> {
    return new Promise<IUser>((resolve, reject) => {
      if (user.password === user.password2) {
        this.getApiService().post<IUser>('/auth/signup', { user: user })
        .then((user: IUser) => {
          return resolve(user)
        })
      } else {
        return reject(null)
      }
    })
  }

  // Update the user on the server(email, pass, etc)
  public updateUser (user: any): Promise<IUser> {
    return new Promise<IUser>((resolve, reject) => {
      this.getApiService().put<IUser>('/user', user.id, { user }).then((user: IUser) => {
        // Update the currentUser observable
        return resolve(user)
      })
    })
  }

  public getApiService (): ApiService {
    if (this.apiService) return this.apiService
    throw ('apiService is null')
  }

  public getUserService (): UserService {
    if (this.userService) return this.userService
    throw ('userService is null')
  }

  public getSessionHandler (): SessionHandler {
    if (this.sessionHandler) return this.sessionHandler
    throw ('sessionHandler is null')
  }
}
